import { request } from "./client";
import type {
  Alert,
  AlertListQuery,
  AlertListResponse,
  AlertRule,
  Device,
  DeviceEventListResponse,
  DeviceEventQuery,
  DeviceListQuery,
  DeviceListResponse,
  DevicePatchQuery,
  DeviceUpdateRequest,
  EnrollmentTokenRequest,
  EnrollmentTokenResponse,
  HealthResponse,
  InventoryReport,
  LoginRequest,
  LoginResponse,
  MetricsQuery,
  MetricsQueryResponse,
  PatchRecordListResponse,
  PatchSummaryResponse,
  SnmpTarget,
  SnmpTargetCreate,
  SnmpTargetListResponse,
  User,
  UserCreateRequest,
  UserUpdateRequest,
} from "./types";

/**
 * Uç nokta katmanı: `openapi.yaml` operasyonları ile birebir eşleşen,
 * tipli istek fonksiyonları. Yollar ve şemalar sözleşmenin kendisidir.
 */
export const api = {
  health: {
    get: () => request<HealthResponse>("/health", { auth: false }),
  },

  auth: {
    login: (body: LoginRequest) =>
      request<LoginResponse>("/auth/login", { method: "POST", body, auth: false }),
    refresh: (refreshToken: string) =>
      request<LoginResponse>("/auth/refresh", {
        method: "POST",
        body: { refreshToken },
        auth: false,
        retryOnUnauthorized: false,
      }),
    /** Sözleşme: POST /auth/logout gövdesi `RefreshRequest`. */
    logout: (refreshToken: string) =>
      request<void>("/auth/logout", {
        method: "POST",
        body: { refreshToken },
      }),
    me: () => request<User>("/auth/me"),
  },

  enrollment: {
    /** POST /enrollment-tokens — tek kullanımlık agent kayıt token'ı üretir. */
    createToken: (body: EnrollmentTokenRequest = {}) =>
      request<EnrollmentTokenResponse>("/enrollment-tokens", { method: "POST", body }),
  },

  devices: {
    list: (query: DeviceListQuery) =>
      request<DeviceListResponse>("/devices", { query: { ...query } }),
    get: (id: string) => request<Device>(`/devices/${id}`),
    update: (id: string, body: DeviceUpdateRequest) =>
      request<Device>(`/devices/${id}`, { method: "PATCH", body }),
    remove: (id: string) => request<void>(`/devices/${id}`, { method: "DELETE" }),
    metrics: (id: string, query: MetricsQuery) =>
      request<MetricsQueryResponse>(`/devices/${id}/metrics`, { query: { ...query } }),
    /** GET /devices/{id}/inventory — en son donanım/OS envanter anlık görüntüsü. */
    inventory: (id: string) => request<InventoryReport>(`/devices/${id}/inventory`),
    /** GET /devices/{id}/events — birleşik olay zaman çizelgesi. */
    events: (id: string, query: DeviceEventQuery = {}) =>
      request<DeviceEventListResponse>(`/devices/${id}/events`, { query: { ...query } }),
    patches: (id: string, query: DevicePatchQuery = {}) =>
      request<PatchRecordListResponse>(`/devices/${id}/patches`, { query: { ...query } }),
  },

  patches: {
    summary: (query: { page?: number; perPage?: number } = {}) =>
      request<PatchSummaryResponse>("/patches/summary", { query: { ...query } }),
  },

  alerts: {
    list: (query: AlertListQuery = {}) =>
      request<AlertListResponse>("/alerts", { query: { ...query } }),
    ack: (id: string) => request<Alert>(`/alerts/${id}/ack`, { method: "POST" }),
  },

  alertRules: {
    list: () => request<AlertRule[]>("/alert-rules"),
    get: (id: string) => request<AlertRule>(`/alert-rules/${id}`),
    create: (body: AlertRule) =>
      request<AlertRule>("/alert-rules", { method: "POST", body }),
    update: (id: string, body: AlertRule) =>
      request<AlertRule>(`/alert-rules/${id}`, { method: "PATCH", body }),
    remove: (id: string) => request<void>(`/alert-rules/${id}`, { method: "DELETE" }),
  },

  snmp: {
    list: (query: { page?: number; perPage?: number } = {}) =>
      request<SnmpTargetListResponse>("/snmp/targets", { query: { ...query } }),
    get: (id: string) => request<SnmpTarget>(`/snmp/targets/${id}`),
    create: (body: SnmpTargetCreate) =>
      request<SnmpTarget>("/snmp/targets", { method: "POST", body }),
    remove: (id: string) => request<void>(`/snmp/targets/${id}`, { method: "DELETE" }),
    metrics: (
      id: string,
      query: Omit<MetricsQuery, "label">,
    ) =>
      request<MetricsQueryResponse>(`/snmp/targets/${id}/metrics`, { query: { ...query } }),
  },

  users: {
    list: () => request<User[]>("/users"),
    create: (body: UserCreateRequest) =>
      request<User>("/users", { method: "POST", body }),
    update: (id: string, body: UserUpdateRequest) =>
      request<User>(`/users/${id}`, { method: "PATCH", body }),
    remove: (id: string) => request<void>(`/users/${id}`, { method: "DELETE" }),
  },
} as const;